import { checkNonce } from 'js/scripts/hash-cash-pow';

/**
 * Solves the Elements HashCash proof-of-work puzzle (see
 * Elements\Bundle\HashCashBundle\Service\HashCashService) for a single JSON
 * submission.
 *
 * A fresh stamp must be fetched and solved before EVERY POST: the server
 * burns each stamp in TmpStore the moment it accepts a request, so reusing
 * the stamp solved for step 1 on the step 2 submit would be rejected as an
 * already-used puzzle. Difficulty is 11 (~2048 hashes), so solving twice per
 * wizard run is effectively instant, but callers should still show a loading
 * state while solve() is pending.
 */
export function useHashCash() {
    async function fetchStamp() {
        let response;
        try {
            // The server derives the stamp from crc32(uri) plus a 60s time window
            // (HashCashService::createStamp()). Two fetches within the same minute would
            // otherwise produce a byte-identical stamp, and the first is already burned as
            // single-use in TmpStore by the time the second is checked.
            //
            // The unique fragment IS sent to the server - that is the point. It sits inside
            // the `uri` query param's value, AjaxController runs crc32() over exactly that
            // string, so a distinct value yields a distinct stamp on every call. Do not
            // "simplify" it away, and do not recompute elhc_uri client-side: the server
            // returns its own crc32 and we echo that back verbatim.
            //
            // The legacy `&time=` in public/static/js/scripts/hashCash.js is NOT precedent for
            // this. It is a separate query param that never enters crc32($uri); it only busts
            // the HTTP cache and does nothing for stamp uniqueness.
            const uniqueUri = location.href + '#' + Date.now() + '-' + Math.random().toString(36).slice(2);
            response = await fetch('/elhc/ajax/create-stamp?uri=' + encodeURIComponent(uniqueUri));
        } catch (e) {
            throw new Error('hashcash-stamp-fetch-failed');
        }
        if (!response.ok) {
            throw new Error('hashcash-stamp-fetch-failed');
        }
        return response.json();
    }

    async function solve() {
        const stamp = await fetchStamp();
        let nonce = 1;
        while (!checkNonce(stamp.elhc_difficulty, stamp.elhc_stamp, nonce)) {
            nonce++;
            if (nonce % 5000 === 0) {
                // yield to the event loop so the UI stays responsive during the (short) search
                await new Promise((resolve) => setTimeout(resolve, 0));
            }
        }

        return {
            elhc_stamp: stamp.elhc_stamp,
            elhc_difficulty: stamp.elhc_difficulty,
            elhc_nonce: nonce,
            // echoed back exactly as the endpoint returned it (a server-computed crc32 of
            // the uri query param) - never recomputed client-side
            elhc_uri: stamp.elhc_uri,
        };
    }

    return { solve };
}
